"use client";

import { useEffect, useState } from "react";
import {
  Facebook,
  Instagram,
  Youtube,
  Twitter,
  Linkedin,
  Music2,
} from "lucide-react";
import Container from "@/components/ui/Container";
import NextSchoolLogo from "@/components/ui/NextSchoolLogo";
import {
  DEFAULT_FOOTER,
  type FooterConfig,
  type SocialPlatform,
  type CmsHeaderScope,
} from "@/lib/cms-types";

const SOCIAL_ICONS: Record<SocialPlatform, { Icon: React.ComponentType<{ size?: number }>; label: string }> = {
  facebook: { Icon: Facebook, label: "Facebook" },
  instagram: { Icon: Instagram, label: "Instagram" },
  youtube: { Icon: Youtube, label: "YouTube" },
  twitter: { Icon: Twitter, label: "Twitter / X" },
  linkedin: { Icon: Linkedin, label: "LinkedIn" },
  tiktok: { Icon: Music2, label: "TikTok" },
};

interface FooterProps {
  config?: FooterConfig;
  /** Zakres konfiguracji stopki — pobiera dedykowaną stopkę landingu. */
  scope?: CmsHeaderScope;
}

export default function Footer({ config, scope }: FooterProps) {
  const [fetchedConfig, setFetchedConfig] = useState<FooterConfig | null>(null);

  useEffect(() => {
    if (config) return;
    let cancelled = false;
    const qs = scope && scope !== "global" ? `?scope=${scope}` : "";
    fetch(`/api/cms${qs}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (!cancelled && data?.footer) setFetchedConfig(data.footer);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [config, scope]);

  const source: FooterConfig = config ?? fetchedConfig ?? DEFAULT_FOOTER;
  const isMatura2027 = scope === "matura2027";
  const normalizeLink = (link: FooterConfig["bottomLinks"][number]) => {
    const label = link.label.trim().toLowerCase();
    if (label === "regulamin") return { ...link, href: "/dokumenty/regulamin" };
    if (label.includes("prywatno") || label === "prywatność") {
      return { ...link, href: "/dokumenty/polityka-prywatnosci" };
    }
    return link;
  };
  const isUnavailableLink = (label: string, href: string) => {
    const normalizedLabel = label.trim().toLowerCase();
    if (href === "#" || /123\s*456\s*789/.test(label) || href === "tel:+48123456789") return true;
    if (["o nas", "cennik", "dla szkół"].includes(normalizedLabel)) return true;
    return /angielski|biologia|chemia/.test(normalizedLabel);
  };
  const active: FooterConfig = isMatura2027
    ? {
        ...source,
        description: "Przygotuj się do matury 2027 z NextSchool.",
        socialLinks: source.socialLinks.filter((social) => social.href !== "#"),
        columns: source.columns
          .map((column) => ({
            ...column,
            links: column.links
              .map(normalizeLink)
              .filter((link) => !isUnavailableLink(link.label, link.href)),
          }))
          .filter((column) => column.links.length > 0),
        bottomLinks: source.bottomLinks
          .map(normalizeLink)
          .filter((link) => !isUnavailableLink(link.label, link.href)),
      }
    : source;
  const year = new Date().getFullYear();
  const copyrightText = active.copyright.replace("{year}", String(year));
  const paymentLogos = (active.paymentLogos ?? []).filter((l) => l.enabled && l.imageUrl);

  return (
    <footer className="bg-slate-900 text-slate-400 pt-16 pb-8">
      <Container>
        <div
          className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-8 pb-12 border-b border-slate-800"
        >
          {/* Brand */}
          <div className="col-span-2 md:col-span-4 lg:col-span-1 mb-4 lg:mb-0">
            <div className="mb-4">
              <NextSchoolLogo size="md" variant="light" />
            </div>
            <p className="text-sm leading-relaxed max-w-xs">{active.description}</p>

            {/* Social Links */}
            <div className="flex gap-3 mt-6 flex-wrap">
              {active.socialLinks.map((social) => {
                const meta = SOCIAL_ICONS[social.platform];
                if (!meta) return null;
                const { Icon, label } = meta;
                return (
                  <a
                    key={social.id}
                    href={social.href}
                    aria-label={label}
                    target={social.href.startsWith("http") ? "_blank" : undefined}
                    rel={social.href.startsWith("http") ? "noopener noreferrer" : undefined}
                    className="w-9 h-9 rounded-lg bg-slate-800 hover:bg-primary flex items-center justify-center transition-colors duration-200"
                  >
                    <Icon size={16} />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Links */}
          {active.columns.map((col) => (
            <div key={col.id}>
              <h4 className="font-semibold text-white text-sm mb-4">{col.title}</h4>
              <ul className="space-y-2.5">
                {col.links.map((link) => (
                  <li key={link.id}>
                    <a
                      href={link.label.toLowerCase() === "kontakt" && link.href === "#" ? "/kontakt" : link.href}
                      target={link.openInNewTab ? "_blank" : undefined}
                      rel={link.openInNewTab ? "noopener noreferrer" : undefined}
                      className="text-sm hover:text-white transition-colors duration-200"
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom */}
        <div className="pt-8 space-y-6">
          {paymentLogos.length > 0 && (
            <div className="flex flex-wrap items-center gap-3">
              <span className="text-xs uppercase tracking-wider text-slate-500 mr-1">
                Bezpieczne płatności
              </span>
              {paymentLogos.map((logo) => (
                <span
                  key={logo.id}
                  className="inline-flex items-center rounded-md bg-white px-2.5 py-1.5"
                >
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src={logo.imageUrl}
                    alt={logo.name}
                    title={logo.name}
                    loading="lazy"
                    className="h-5 w-auto object-contain"
                  />
                </span>
              ))}
            </div>
          )}
          <div className="flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-slate-500">
            <p>{copyrightText}</p>
            <div className="flex gap-6 flex-wrap">
              {active.bottomLinks.map((link) => (
                <a
                  key={link.id}
                  href={link.href}
                  target={link.openInNewTab ? "_blank" : undefined}
                  rel={link.openInNewTab ? "noopener noreferrer" : undefined}
                  className="hover:text-white transition-colors"
                >
                  {link.label}
                </a>
              ))}
            </div>
          </div>
        </div>
      </Container>
    </footer>
  );
}
